import React, { useState } from 'react';
import { ChefHat, Compass, Search, Sparkles, Target } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Empty, Field, Input, Loading, Page, PageHead, Select, Tabs,
} from '../components/ui';
import { api } from '../lib/api';
import { n0, n1 } from '../lib/format';

const GOALS = [
  { value: 'general_health', label: 'General health' },
  { value: 'muscle_building', label: 'Muscle building' },
  { value: 'weight_loss', label: 'Weight loss' },
  { value: 'endurance', label: 'Endurance' },
];

const MODES = [
  { value: 'ingredient', label: 'Whole foods' },
  { value: 'branded', label: 'Branded products' },
];

// Half whole foods, half things that only resolve because of the Open Food
// Facts merge — 'paneer' and 'dal' returned nothing useful from USDA alone.
const EXAMPLES = ['chicken', 'salmon', 'broccoli', 'paneer', 'dal', 'greek yogurt'];

const MACRO_BARS = [
  { key: 'Protein', color: 'var(--macro-protein)', max: 60 },
  { key: 'Carbs', color: 'var(--macro-carbs)', max: 80 },
  { key: 'Fat', color: 'var(--macro-fats)', max: 45 },
];

// Where a food's numbers came from. USDA figures are laboratory assays; Open
// Food Facts figures are read off a package by whoever added the product. Both
// are worth having and they are not the same kind of evidence, so the card says
// which one it is rather than presenting them as interchangeable.
const SOURCES = {
  usda: { label: 'USDA', title: 'USDA FoodData Central — laboratory-measured' },
  openfoodfacts: { label: 'OFF', title: 'Open Food Facts — transcribed from the package label' },
};

function FoodCard({ food }) {
  const source = SOURCES[food.source];
  return (
    <div className="food">
      <div className="food-name">{food.description}</div>
      <div className="spread">
        <span className="num" style={{ fontSize: 19, fontWeight: 700 }}>
          {n0(food.calories)}<span className="faint" style={{ fontSize: 11, fontWeight: 500 }}> kcal</span>
        </span>
        <span className="row-tight" style={{ gap: 6 }}>
          {source && <Badge tone="muted" title={source.title}>{source.label}</Badge>}
          <span className="faint" style={{ fontSize: 11 }}>per 100 g</span>
        </span>
      </div>
      <div className="stack-tight">
        {MACRO_BARS.map((m) => (
          <div key={m.key} className="row-tight" style={{ gap: 8 }}>
            <span className="faint" style={{ fontSize: 11, width: 46, flex: 'none' }}>{m.key}</span>
            <span className="bar grow">
              <span style={{ width: `${Math.min(100, (food[m.key] / m.max) * 100)}%`, background: m.color }} />
            </span>
            <span className="num" style={{ fontSize: 11.5, width: 40, textAlign: 'right', flex: 'none' }}>
              {n1(food[m.key])}g
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DietAI() {
  const [mode, setMode] = useState('macro');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [macro, setMacro] = useState({ calories: 300, protein: 30, fat: 10, carbs: 20, goal: 'muscle_building', mode: 'ingredient' });
  const [macroResults, setMacroResults] = useState(null);

  const [query, setQuery] = useState('');
  const [discoverResults, setDiscoverResults] = useState(null);

  const [recipes, setRecipes] = useState(null);
  const [basis, setBasis] = useState(null);

  const run = async (fn) => {
    setError('');
    setBusy(true);
    try { await fn(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const searchMacro = () => run(async () => {
    const res = await api.dietAI.macro({
      calories: parseFloat(macro.calories) || 0,
      protein: parseFloat(macro.protein) || 0,
      fat: parseFloat(macro.fat) || 0,
      carbs: parseFloat(macro.carbs) || 0,
      goal: macro.goal,
      mode: macro.mode,
    });
    setMacroResults(res.recommendations || []);
  });

  const discover = (term) => run(async () => {
    const q = term ?? query;
    if (!q.trim()) return;
    setQuery(q);
    const res = await api.dietAI.discover({ food_name: q.trim(), n: 12 });
    setDiscoverResults(res.recommendations || []);
  });

  const loadRecipes = () => run(async () => {
    const res = await api.dietAI.collab({ user_id: 1, n: 12 });
    setRecipes(res.recommendations || []);
    setBasis(res.personalised_from);
  });

  return (
    <Page>
      <PageHead
        title="Diet AI"
        actions={
          <Tabs
            value={mode}
            onChange={setMode}
            options={[
              { value: 'macro', label: 'Macro search' },
              { value: 'discover', label: 'Discover' },
              { value: 'recipes', label: 'For you' },
            ]}
          />
        }
      >
        Three ways to find food: match a macro target across 2.9 million foods, find things similar to
        something you like, or get recipes shaped by what you have been logging.
      </PageHead>

      {error && <Alert tone="crit">{error}</Alert>}

      {mode === 'macro' && (
        <>
          <Card title="Macro target" sub="Nearest neighbours in calorie and macro space, weighted by your goal">
            <div className="grid grid-4">
              <Field label="Calories · kcal">
                <Input numeric type="number" value={macro.calories}
                  onChange={(e) => setMacro({ ...macro, calories: e.target.value })} />
              </Field>
              <Field label="Protein · g">
                <Input numeric type="number" value={macro.protein}
                  onChange={(e) => setMacro({ ...macro, protein: e.target.value })} />
              </Field>
              <Field label="Carbs · g">
                <Input numeric type="number" value={macro.carbs}
                  onChange={(e) => setMacro({ ...macro, carbs: e.target.value })} />
              </Field>
              <Field label="Fat · g">
                <Input numeric type="number" value={macro.fat}
                  onChange={(e) => setMacro({ ...macro, fat: e.target.value })} />
              </Field>
            </div>
            <div className="grid grid-2" style={{ marginTop: 14 }}>
              <Field label="Goal" hint="Changes how each macro is weighted in the distance">
                <Select options={GOALS} value={macro.goal}
                  onChange={(e) => setMacro({ ...macro, goal: e.target.value })} />
              </Field>
              <Field label="Dataset" hint="15,003 whole foods or 2,933,459 branded products">
                <Select options={MODES} value={macro.mode}
                  onChange={(e) => setMacro({ ...macro, mode: e.target.value })} />
              </Field>
            </div>
            <Button variant="primary" icon={Target} onClick={searchMacro} disabled={busy}
              loading={busy} style={{ marginTop: 16 }}>
              Find matching foods
            </Button>
          </Card>

          {busy && <Card><Loading label="Searching the food table…" /></Card>}
          {!busy && macroResults && (
            macroResults.length === 0
              ? <Card><Empty icon={Target} title="Nothing matched">Try widening the macro target.</Empty></Card>
              : <div className="grid grid-3">{macroResults.map((f, i) => <FoodCard key={i} food={f} />)}</div>
          )}
          {!busy && !macroResults && (
            <Card><Empty icon={Target} title="Set a target above">
              Results appear here, closest match first.
            </Empty></Card>
          )}
        </>
      )}

      {mode === 'discover' && (
        <>
          <Card title="Find similar foods" sub="Content similarity over food descriptions">
            <div className="row" style={{ gap: 10 }}>
              <Input
                className="input grow"
                placeholder="A food you already eat — chicken, oats, salmon…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && discover()}
                style={{ flex: 1, minWidth: 220 }}
              />
              <Button variant="primary" icon={Search} onClick={() => discover()} disabled={busy} loading={busy}>
                Search
              </Button>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <span className="label" style={{ marginRight: 2 }}>Try</span>
              {EXAMPLES.map((e) => (
                <Button key={e} size="sm" variant="quiet" onClick={() => discover(e)} disabled={busy}>
                  {e}
                </Button>
              ))}
            </div>
          </Card>

          {busy && <Card><Loading label="Looking for similar foods…" /></Card>}
          {!busy && discoverResults && (
            discoverResults.length === 0
              ? <Card><Empty icon={Compass} title={`Nothing similar to "${query}"`}>
                  Try a simpler term — a single ingredient works best.
                </Empty></Card>
              : <div className="grid grid-3">{discoverResults.map((f, i) => <FoodCard key={i} food={f} />)}</div>
          )}
          {!busy && !discoverResults && (
            <Card><Empty icon={Compass} title="Search for a food">
              Enter something you eat often and see nutritionally similar alternatives.
            </Empty></Card>
          )}
        </>
      )}

      {mode === 'recipes' && (
        <>
          <Card
            title="Recipes for you"
            sub="Matrix factorisation over 794,473 ratings, steered by your own food log"
            actions={
              <Button variant="primary" icon={Sparkles} onClick={loadRecipes} disabled={busy} loading={busy}>
                {recipes ? 'Refresh' : 'Get recommendations'}
              </Button>
            }
          >
            {basis && (
              <Alert tone={basis === 'food_log' ? 'ok' : 'info'}>
                {basis === 'food_log'
                  ? 'Personalised from the foods you have logged.'
                  : basis === 'ratings_matrix'
                    ? 'Personalised from the ratings matrix.'
                    : 'Showing popular recipes — log a few meals and these become personal to you.'}
              </Alert>
            )}
            {!basis && !busy && (
              <p className="dim" style={{ fontSize: 13 }}>
                Recommendations are built from what you log. The more meals you record, the more these
                shift toward your own tastes.
              </p>
            )}
          </Card>

          {busy && <Card><Loading label="Scoring the recipe catalogue…" /></Card>}
          {!busy && recipes && (
            recipes.length === 0
              ? <Card><Empty icon={ChefHat} title="No recommendations yet" /></Card>
              : (
                <div className="grid grid-3">
                  {recipes.map((r, i) => (
                    <div className="food" key={i}>
                      <div className="row-tight" style={{ justifyContent: 'space-between' }}>
                        <Badge tone="accent">#{i + 1}</Badge>
                        {r.ratings != null && (
                          <span className="faint" style={{ fontSize: 11 }}>
                            <span className="num">{n0(r.ratings)}</span> ratings
                          </span>
                        )}
                      </div>
                      <div className="food-name" style={{ textTransform: 'capitalize', fontSize: 13.5 }}>
                        {r.recipe_name}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}
        </>
      )}
    </Page>
  );
}
